// Chart dimension constants.
// The plot area itself must be SQUARE: RS-Ratio and RS-Momentum share one
// domain, so a stretched box distorts every angle of rotation. Width and
// height are therefore derived from PLOT_SIZE plus the margins, never set
// independently. Axes sit on the bottom and the right, so the left margin only
// needs breathing room, not tick space.
const PLOT_SIZE = 760
export const CHART_MARGIN = { top: 24, right: 56, bottom: 44, left: 24 }
export const CHART_WIDTH = PLOT_SIZE + CHART_MARGIN.left + CHART_MARGIN.right
const CHART_HEIGHT = PLOT_SIZE + CHART_MARGIN.top + CHART_MARGIN.bottom
const SLIDER_HEIGHT = 76

// Both SVGs scale to the container's width, so the height of the whole block
// is a fixed multiple of that width. The controller inverts this to size the
// chart from the space it has above the fold (rrg_chart_controller#fitToFold).
export const CHART_BLOCK_HEIGHT_RATIO = (CHART_HEIGHT + SLIDER_HEIGHT) / CHART_WIDTH

// Theme-aware chrome. These are CSS custom properties rather than hex values so
// the chart follows [data-bs-theme]; they must be applied with .style(), not
// .attr() — var() is only resolved in CSS, not in presentation attributes.
const AXIS_COLOR = "var(--bs-secondary-color)"
const GRID_COLOR = "var(--bs-border-color)"
const QUADRANT_FILLS = {
  leading: "var(--app-rrg-leading-bg)",
  weakening: "var(--app-rrg-weakening-bg)",
  lagging: "var(--app-rrg-lagging-bg)",
  improving: "var(--app-rrg-improving-bg)"
}
const QUADRANT_LABEL_COLORS = {
  leading: "var(--app-rrg-leading-fg)",
  weakening: "var(--app-rrg-weakening-fg)",
  lagging: "var(--app-rrg-lagging-fg)",
  improving: "var(--app-rrg-improving-fg)"
}

// Scale domain (RS-Ratio and RS-Momentum range)
const SCALE_DOMAIN = [95, 105]

// Quadrant threshold and colors
const QUADRANT_THRESHOLD = 100
const QUADRANT_COLORS = {
  leading: "#90EE90",
  weakening: "#FFD700",
  lagging: "#FFA07A",
  improving: "#87CEFA"
}

/**
 * Determine quadrant from coordinates
 */
export function getQuadrant(px, py) {
  if (px >= QUADRANT_THRESHOLD && py >= QUADRANT_THRESHOLD) return "leading"
  if (px >= QUADRANT_THRESHOLD && py < QUADRANT_THRESHOLD) return "weakening"
  if (px < QUADRANT_THRESHOLD && py < QUADRANT_THRESHOLD) return "lagging"
  return "improving"
}

/**
 * Catmull-Rom spline interpolation for smooth curves with quadrant colors.
 * Takes data points and generates additional interpolated points for smooth rendering.
 *
 * @param {Array} points - Array of {x, y} data points
 * @param {number} numInterpolated - Number of interpolated points between each pair
 * @returns {Array} Array of interpolated points with {x, y, color}
 */
export function catmullRomSpline(points, numInterpolated = 8) {
  if (points.length < 2) return points

  const result = []

  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)]
    const p1 = points[i]
    const p2 = points[i + 1]
    const p3 = points[Math.min(points.length - 1, i + 2)]

    // Add the start point
    if (i === 0) {
      result.push({ x: p1.x, y: p1.y, color: QUADRANT_COLORS[getQuadrant(p1.x, p1.y)] })
    }

    // Interpolate between p1 and p2 using Catmull-Rom basis functions
    for (let t = 1; t <= numInterpolated; t++) {
      const s = t / numInterpolated
      const s2 = s * s
      const s3 = s2 * s

      const px = 0.5 * ((2 * p1.x) +
        (-p0.x + p2.x) * s +
        (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * s2 +
        (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * s3)

      const py = 0.5 * ((2 * p1.y) +
        (-p0.y + p2.y) * s +
        (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * s2 +
        (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * s3)

      result.push({
        x: px,
        y: py,
        color: QUADRANT_COLORS[getQuadrant(px, py)]
      })
    }
  }

  return result
}

/**
 * Split a polyline into runs of consecutive segments sharing the same color,
 * so each run can be drawn as a single path instead of one path per segment.
 * Segment i (points[i] -> points[i+1]) takes the color of points[i].
 *
 * @param {Array} points - Array of {x, y, color} points
 * @returns {Array} Array of {color, points} runs covering every segment
 */
export function colorRuns(points) {
  const runs = []
  if (points.length < 2) return runs

  let start = 0
  for (let i = 1; i < points.length; i++) {
    if (i === points.length - 1 || points[i].color !== points[start].color) {
      runs.push({ color: points[start].color, points: points.slice(start, i + 1) })
      start = i
    }
  }

  return runs
}

/**
 * Draw smooth quadrant-colored tails for each sector, one path per color run.
 *
 * @param {Object} tailsGroup - D3 selection of the tails <g> element
 * @param {Array} historical - Historical points ({id, day, x, y}) for all sectors
 * @param {Function} line - D3 line generator
 */
function renderTails(tailsGroup, historical, line) {
  const sectorData = {}
  historical.forEach(d => {
    if (!sectorData[d.id]) sectorData[d.id] = []
    sectorData[d.id].push(d)
  })

  Object.values(sectorData).forEach(points => {
    const sectorHistory = points.sort((a, b) => a.day - b.day)
    if (sectorHistory.length < 2) return

    const smoothPoints = catmullRomSpline(sectorHistory)
    colorRuns(smoothPoints).forEach(run => {
      tailsGroup.append("path")
        .datum(run.points)
        .attr("fill", "none")
        .attr("stroke", run.color)
        .attr("stroke-width", 2)
        .attr("stroke-opacity", 0.7)
        .attr("d", line)
    })
  })
}

/**
 * Create X and Y scales for the RRG chart.
 * Clamped so extreme values pin to the plot edge instead of drawing outside it.
 * @param {Object} d3 - D3 library reference
 * @returns {Object} Object with x and y scale functions
 */
function createScales(d3) {
  const x = d3.scaleLinear()
    .domain(SCALE_DOMAIN)
    .range([CHART_MARGIN.left, CHART_WIDTH - CHART_MARGIN.right])
    .clamp(true)

  const y = d3.scaleLinear()
    .domain(SCALE_DOMAIN)
    .range([CHART_HEIGHT - CHART_MARGIN.bottom, CHART_MARGIN.top])
    .clamp(true)

  return { x, y }
}

/**
 * Creates a Relative Rotation Graph using D3
 *
 * @param {Object} processedData - Data for the RRG chart
 * @returns {SVGElement} - SVG element containing the RRG chart
 */
export function createRrgChart(processedData) {
  const d3 = window.d3;
  const { x, y } = createScales(d3)
  const width = CHART_WIDTH
  const height = CHART_HEIGHT
  const margin = CHART_MARGIN

  // Create the SVG container
  const svg = d3.create("svg")
    .attr("viewBox", [0, 0, width, height])
    .attr("width", "100%")
    .attr("style", "display: block; width: 100%; height: auto; font-family: inherit; border: none;");

  // Add grid lines
  const xGrid = g => g
    .style("stroke", GRID_COLOR)
    .attr("stroke-opacity", 0.5)
    .attr("shape-rendering", "crispEdges")
    .call(d3.axisBottom(x)
      .tickSize(-(height - margin.top - margin.bottom))
      .tickFormat("")
      .ticks(10)
    );
    
  const yGrid = g => g
    .style("stroke", GRID_COLOR)
    .attr("stroke-opacity", 0.5)
    .attr("shape-rendering", "crispEdges")
    .call(d3.axisLeft(y)
      .tickSize(-(width - margin.left - margin.right))
      .tickFormat("")
      .ticks(10)
    );
  
  // Add grid
  svg.append("g")
    .attr("transform", `translate(0,${height - margin.bottom})`)
    .call(xGrid);
    
  svg.append("g")
    .attr("transform", `translate(${margin.left},0)`)
    .call(yGrid);
  
  // Quadrant backgrounds. Each rect spans from the (100, 100) centre out to the
  // matching corner of the square plot area.
  const quadrantRects = [
    { key: "leading",   x: x(100),      y: margin.top },
    { key: "weakening", x: x(100),      y: y(100) },
    { key: "lagging",   x: margin.left, y: y(100) },
    { key: "improving", x: margin.left, y: margin.top }
  ];

  quadrantRects.forEach(({ key, x: rectX, y: rectY }) => {
    svg.append("rect")
      .attr("x", rectX)
      .attr("y", rectY)
      .attr("width", rectX === margin.left ? x(100) - margin.left : width - margin.right - x(100))
      .attr("height", rectY === margin.top ? y(100) - margin.top : height - margin.bottom - y(100))
      .style("fill", QUADRANT_FILLS[key]);
  });

  // Add quadrant labels with consistent positioning
  const labelPadding = 25; // Consistent padding from edges
  const fontSize = 15; // Font size for consistency

  const quadrantLabels = [
    { key: "leading",   text: "LEADING",   anchor: "end",   x: width - margin.right - labelPadding, y: margin.top + labelPadding + fontSize / 2 },
    { key: "improving", text: "IMPROVING", anchor: "start", x: margin.left + labelPadding,          y: margin.top + labelPadding + fontSize / 2 },
    { key: "weakening", text: "WEAKENING", anchor: "end",   x: width - margin.right - labelPadding, y: height - margin.bottom - labelPadding - fontSize / 2 },
    { key: "lagging",   text: "LAGGING",   anchor: "start", x: margin.left + labelPadding,          y: height - margin.bottom - labelPadding - fontSize / 2 }
  ];

  quadrantLabels.forEach(label => {
    svg.append("text")
      .attr("x", label.x)
      .attr("y", label.y)
      .attr("text-anchor", label.anchor)
      .attr("font-size", `${fontSize}px`)
      .attr("letter-spacing", "0.08em")
      .attr("dominant-baseline", "middle")
      .attr("font-weight", "bold")
      .style("fill", QUADRANT_LABEL_COLORS[label.key])
      .text(label.text);
  });

  // Add axes at bottom and right sides
  // X-axis (bottom)
  svg.append("g")
    .attr("transform", `translate(0,${height - margin.bottom})`)
    .attr("class", "x-axis")
    .call(d3.axisBottom(x)
      .ticks(10)
      .tickSize(0)) // Remove ticks
    .call(g => g.select(".domain").style("stroke", AXIS_COLOR).attr("stroke-width", 1))
    .call(g => g.selectAll(".tick text").style("fill", AXIS_COLOR).attr("font-size", "10px"));

  // RS-Ratio label (inside chart)
  svg.append("text")
    .attr("x", (margin.left + width - margin.right) / 2)
    .attr("y", height - margin.bottom - 20)
    .attr("text-anchor", "middle")
    .attr("font-weight", "bold")
    .attr("font-size", "11px")
    .style("fill", AXIS_COLOR)
    .text("RS-Ratio  (relative strength vs benchmark →)");
  
  // Y-axis (right)
  svg.append("g")
    .attr("transform", `translate(${width - margin.right},0)`)
    .attr("class", "y-axis")
    .call(d3.axisRight(y)
      .ticks(10)
      .tickSize(0)) // Remove ticks
    .call(g => g.select(".domain").style("stroke", AXIS_COLOR).attr("stroke-width", 1))
    .call(g => g.selectAll(".tick text").style("fill", AXIS_COLOR).attr("font-size", "10px"));

  // RS-Momentum label (just inside the colored chart area, vertical)
  svg.append("text")
    .attr("transform", "rotate(-90)")
    .attr("x", -height/2)
    .attr("y", width - margin.right - 15) // Adjusted to be just inside the chart, accounting for font height
    .attr("text-anchor", "middle")
    .attr("font-weight", "bold")
    .attr("font-size", "11px")
    .style("fill", AXIS_COLOR)
    .text("RS-Momentum  (rate of change ↑)");
  
  // Create a color scale for sectors
  const color = d3.scaleOrdinal()
    .domain(processedData.current.map(d => d.id))
    .range(d3.schemeCategory10);
  
  // Create line generator for tail paths
  const line = d3.line()
    .x(d => x(d.x))
    .y(d => y(d.y))
    .curve(d3.curveLinear);

  // Add trails for each sector
  const tailsGroup = svg.append("g").attr("class", "rrg-tails");
  renderTails(tailsGroup, processedData.historical, line);

  // Add a tooltip div if it doesn't exist yet
  let tooltip = d3.select("body").select(".rrg-tooltip");
  if (tooltip.empty()) {
    // Presentation lives in components/_tooltips.scss (.rrg-tooltip) so the
    // tooltip follows the light/dark theme — only opacity/position are set here.
    tooltip = d3.select("body").append("div")
      .attr("class", "rrg-tooltip")
      .style("opacity", 0);
  }
  
  // Add points for current position of each sector with mouseover effects
  svg.append("g")
    .attr("class", "rrg-dots")
    .selectAll("circle")
    .data(processedData.current, d => d.id)
    .join("circle")
      .attr("cx", d => x(d.x))
      .attr("cy", d => y(d.y))
      .attr("r", 9)
      .attr("fill", d => d.color || color(d.id))
      .attr("stroke", "white")
      .attr("stroke-width", 1.5)
      .on("mouseover", function(event, d) {
        // Highlight on hover
        d3.select(this)
          .attr("r", 11)
          .attr("stroke-width", 2);

        // Show tooltip with position
        tooltip
          .style("opacity", 1)
          .html(`
            <strong>${d.id}</strong>${d.quadrant ? ` — ${d.quadrant}` : ""}<br>
            RS-Ratio: ${typeof d.x === 'number' ? d.x.toFixed(1) : d.x}<br>
            RS-Momentum: ${typeof d.y === 'number' ? d.y.toFixed(1) : d.y}<br>
            ${d.date ? `Date: ${d.date}` : ""}
          `)
          .style("left", (event.pageX + 10) + "px")
          .style("top", (event.pageY - 28) + "px");
      })
      .on("mouseout", function() {
        // Reset on mouseout
        d3.select(this)
          .attr("r", 9)
          .attr("stroke-width", 1.5);
          
        // Hide tooltip
        tooltip.style("opacity", 0);
      });
  
  // Add sector labels next to current points
  svg.append("g")
    .attr("class", "rrg-labels")
    .selectAll("text")
    .data(processedData.current, d => d.id)
    .join("text")
      .attr("x", d => x(d.x) + 12)
      .attr("y", d => y(d.y) + 4)
      .text(d => d.id)
      .attr("font-size", "11px")
      .attr("font-weight", "bold")
      .attr("fill", d => d.color || color(d.id));
  
  
  return svg.node();
}

/**
 * Updates an existing RRG chart with new data using smooth transitions
 *
 * @param {SVGElement} svgNode - The existing SVG element
 * @param {Object} processedData - New data for the RRG chart
 * @param {number} transitionDuration - Duration of transition in ms (default: 150)
 */
export function updateRrgChart(svgNode, processedData, transitionDuration = 150) {
  const d3 = window.d3;
  const svg = d3.select(svgNode);
  const { x, y } = createScales(d3)

  const color = d3.scaleOrdinal()
    .domain(processedData.current.map(d => d.id))
    .range(d3.schemeCategory10);

  // Update dots with transition
  const dots = svg.select(".rrg-dots")
    .selectAll("circle")
    .data(processedData.current, d => d.id);

  dots.transition()
    .duration(transitionDuration)
    .ease(d3.easeLinear)
    .attr("cx", d => x(d.x))
    .attr("cy", d => y(d.y))
    .attr("fill", d => d.color || color(d.id));

  // Update labels with transition
  const labels = svg.select(".rrg-labels")
    .selectAll("text")
    .data(processedData.current, d => d.id);

  labels.transition()
    .duration(transitionDuration)
    .ease(d3.easeLinear)
    .attr("x", d => x(d.x) + 12)
    .attr("y", d => y(d.y) + 4)
    .attr("fill", d => d.color || color(d.id));

  // Update tails (lines) - these change structure so we rebuild them
  const tailsGroup = svg.select(".rrg-tails");
  tailsGroup.selectAll("*").remove();

  const line = d3.line()
    .x(d => x(d.x))
    .y(d => y(d.y))
    .curve(d3.curveLinear);

  renderTails(tailsGroup, processedData.historical, line);
}

/**
 * Creates the time slider component for the RRG chart
 *
 * @param {HTMLElement} container - The DOM container element
 * @param {Object} data - Data for the slider
 * @param {Function} updateChartCallback - Callback to update the chart based on selection
 * @returns {Object} - References to created elements
 */
export function createRrgSlider(container, data, updateChartCallback) {
  const d3 = window.d3;

  // Slider dimensions mirror the chart's so the brush lines up horizontally
  // with the plot area sitting underneath it.
  const sliderWidth = CHART_WIDTH;
  const sliderHeight = SLIDER_HEIGHT;
  const sliderMargin = {top: 16, right: CHART_MARGIN.right, bottom: 22, left: CHART_MARGIN.left};

  // Use the end date from data, or fall back to today
  const referenceDate = data.endDate ? new Date(data.endDate) : new Date();
  const tailLength = data.tailLength || 10;

  // Create time slider SVG
  const sliderSvg = d3.select(container).append("svg")
    .attr("viewBox", [0, 0, sliderWidth, sliderHeight])
    .attr("width", "100%")
    .attr("class", "rrg-slider")
    .attr("style", "display: block; width: 100%; height: auto; font-family: inherit;");

  // Add a clipping path to prevent the line from extending outside the chart area
  sliderSvg.append("defs")
    .append("clipPath")
    .attr("id", "slider-clip")
    .append("rect")
    .attr("x", sliderMargin.left)
    .attr("y", sliderMargin.top)
    .attr("width", sliderWidth - sliderMargin.left - sliderMargin.right)
    .attr("height", sliderHeight - sliderMargin.top - sliderMargin.bottom);

  // The caption is HTML, not SVG text: the chart scales with its container, and
  // anything inside the SVG scales with it — a 12px label renders at 20px+ on a
  // wide monitor. HTML keeps it at page type size.
  //
  // It goes in the container's PARENT, so it spans the card even when the chart
  // itself has been narrowed to fit the fold. Inside the container it would
  // wrap to two lines at exactly the sizes where vertical space is scarcest.
  const header = d3.select(container.parentNode || container)
    .insert("div", ":first-child")
    .attr("class", "rrg-slider-header")

  header.append("span")
    .attr("class", "rrg-slider-hint")
    .text("Drag the grey window to change the period")

  const timePeriodText = header.append("span")
    .attr("class", "rrg-slider-range time-period-text")

  // Convert day offsets to dates for the slider using reference date
  const dayToDate = day => {
    const result = new Date(referenceDate);
    result.setDate(referenceDate.getDate() + day);
    return result;
  };

  // Calculate day range for slider based on tail length
  // tail_length is in trading days; convert to calendar days (~7/5 ratio)
  const defaultMinDay = -Math.ceil(tailLength * 7 / 5);
  const minDay = Math.min(...(data.days || [-30]), defaultMinDay); // Use actual data range or default
  const maxDay = Math.max(...(data.days || [0]), 0); // Default to at least current day (0 = end date)

  // Benchmark price history drives the slider's trend line and date domain.
  // Without it the slider still works over the historical day range; it just
  // has no trend line.
  const timeData = (data.benchmark || [])
    .map(point => ({ date: new Date(point.date), value: point.value }))
    .sort((a, b) => a.date - b.date)

  // Scales for the time slider - use actual benchmark date range if available,
  // otherwise fall back to the historical day range
  const xDomain = timeData.length > 0
    ? [timeData[0].date, timeData[timeData.length - 1].date]
    : [dayToDate(minDay), dayToDate(maxDay)];
  
  const x = d3.scaleTime()
    .domain(xDomain)
    .range([sliderMargin.left, sliderWidth - sliderMargin.right]);

  // Set a tighter y-scale range to ensure the line stays within bounds
  // For benchmark data, use a domain that keeps the line well within the visible area
  const yMin = timeData.length > 0 ? Math.min(98, d3.min(timeData, d => d.value) - 0.2) : 98;
  const yMax = timeData.length > 0 ? Math.max(102, d3.max(timeData, d => d.value) + 0.2) : 102;

  const y = d3.scaleLinear()
    .domain([yMin, yMax])
    .range([sliderHeight - sliderMargin.bottom, sliderMargin.top]);

  // Add the benchmark trend line and its label when price history is available
  if (timeData.length > 0) {
    const line = d3.line()
      .x(d => x(d.date))
      .y(d => y(d.value))
      .curve(d3.curveCatmullRom);

    // Clipping keeps the line inside the chart area
    sliderSvg.append("g")
      .attr("clip-path", "url(#slider-clip)")
      .append("path")
      .datum(timeData)
      .attr("fill", "none")
      .style("stroke", "var(--bs-secondary-color)")
      .attr("stroke-width", 1.5)
      .attr("d", line);

    sliderSvg.append("text")
      .attr("x", sliderMargin.left + 4)
      .attr("y", sliderMargin.top + 9)
      .attr("text-anchor", "start")
      .attr("font-size", "10px")
      .style("fill", AXIS_COLOR)
      .text("Benchmark index");
  }

  // Add x-axis with dates
  sliderSvg.append("g")
    .attr("transform", `translate(0,${sliderHeight - sliderMargin.bottom})`)
    .call(d3.axisBottom(x).ticks(6).tickFormat(d3.timeFormat("%b %d")))
    .call(g => g.select(".domain").style("stroke", GRID_COLOR))
    .call(g => g.selectAll(".tick line").style("stroke", GRID_COLOR))
    .call(g => g.selectAll(".tick text").style("fill", AXIS_COLOR).attr("font-size", "10px"));

  // Set initial date range for brush based on tail length
  // tailLength is in trading days, convert to calendar days (~7/5 ratio)
  const tailLengthInDays = Math.max(7, Math.ceil(tailLength * 7 / 5));

  // Use actual data bounds for default brush position to ensure it's visible
  const dataEndDate = timeData.length > 0 ? timeData[timeData.length - 1].date : dayToDate(0);
  const dataStartDate = timeData.length > 0 ? timeData[0].date : dayToDate(minDay);

  // Calculate default start date as tailLengthInDays before the data end
  const defaultEndDate = dataEndDate;
  const defaultStartDate = new Date(dataEndDate);
  defaultStartDate.setDate(defaultStartDate.getDate() - tailLengthInDays);

  // Clamp to available data range
  if (defaultStartDate < dataStartDate) {
    defaultStartDate.setTime(dataStartDate.getTime());
  }

  // Calculate day values for the callbacks
  const defaultEndDay = Math.round((dataEndDate - referenceDate) / (24 * 60 * 60 * 1000));
  const defaultStartDay = Math.round((defaultStartDate - referenceDate) / (24 * 60 * 60 * 1000));

  // Set initial time period display text
  timePeriodText.text(`${d3.timeFormat("%b %d, %Y")(defaultStartDate)} — ${d3.timeFormat("%b %d, %Y")(defaultEndDate)}`);

  // Function to handle brush events
  function brushed(event) {
    if (!event.selection) return;

    // Convert brush selection to dates and then to days
    const [x0, x1] = event.selection.map(x.invert);

    // Calculate days relative to reference date (end date from form)
    const startDay = Math.round((x0 - referenceDate) / (24 * 60 * 60 * 1000));
    const endDay = Math.round((x1 - referenceDate) / (24 * 60 * 60 * 1000));

    // Clamp to available days
    let clampedStartDay = Math.max(minDay, startDay);
    let clampedEndDay = Math.min(maxDay, endDay);

    // If clamped range is invalid (start > end), adjust to use the nearest valid range
    // This happens when slider is outside the historical data range
    if (clampedStartDay > clampedEndDay) {
      // Use the edge of the data range that's closest to the selected range
      if (endDay < minDay) {
        // Selection is entirely before data range - use earliest data
        clampedStartDay = minDay;
        clampedEndDay = minDay;
      } else {
        // Selection is entirely after data range - use latest data
        clampedStartDay = maxDay;
        clampedEndDay = maxDay;
      }
    }

    // Update the chart using the callback
    if (event.type === "end") {
      // On brush end event, force immediate update
      updateChartCallback(clampedStartDay, clampedEndDay);
    } else {
      // During brushing, use a small delay to reduce jitter
      clearTimeout(brushTimer);
      brushTimer = setTimeout(() => {
        updateChartCallback(clampedStartDay, clampedEndDay);
      }, 10);
    }

    // Update time period display
    const startDate = dayToDate(clampedStartDay);
    const endDateDisplay = dayToDate(clampedEndDay);
    const formatter = d3.timeFormat("%b %d, %Y");
    timePeriodText.text(`${formatter(startDate)} — ${formatter(endDateDisplay)}`);
  }
  
  // Flag to skip initial brush event (prevents double-render on init)
  let initialized = false

  // Debounce timer for chart updates while brushing
  let brushTimer = null

  // Add brush for time selection
  const brush = d3.brushX()
    .extent([[sliderMargin.left, sliderMargin.top], [sliderWidth - sliderMargin.right, sliderHeight - sliderMargin.bottom]])
    .on("brush", (event) => {
      if (initialized) brushed(event)
    })
    .on("end", (event) => {
      if (initialized) brushed(event)
    });

  // Brush window and handle styling live in components/_charts.scss (.brush)
  // so they follow the theme.
  const brushGroup = sliderSvg.append("g")
    .attr("class", "brush")
    .call(brush);

  // Set initial brush position (this triggers brush event, but we skip it via initialized flag)
  brushGroup.call(brush.move, [x(defaultStartDate), x(defaultEndDate)]);

  // Initialize the chart once after a short delay
  setTimeout(() => {
    initialized = true
    updateChartCallback(defaultStartDay, defaultEndDay)
  }, 100);
  
  return {
    sliderSvg,
    timePeriodText,
    brushGroup
  };
}